import { useEffect, useState } from "react";

import { getZendeskJWTMessaging } from "../api/zendesk";
import meta from "src/utils/meta";

type JwtFnCallback = (token: string) => void;

// Adopted from https://gist.github.com/wuservices/c0c3de056f5e528c9145b1df128b5c83
interface ZendeskMessagingWidget {
  (type: "messenger", command: "loginUser", callbackCaller: (callback: JwtFnCallback) => unknown);
  (type: "messenger", command: "logoutUser"): void;
  (type: "messenger:set", setting: "zIndex", newZIndex: number): void;
}

declare global {
  interface Window {
    zE: ZendeskMessagingWidget;
  }
}

const useZendeskMessaging = (
  isFacility: boolean,
  admin: boolean
): { webWidgetRendered: boolean } => {
  const [webWidgetRendered, setWebWidgetRendered] = useState<boolean>(false);

  // Doc ref - https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/sdk_api_reference/#authentication
  useEffect(() => {
    if (!webWidgetRendered) {
      return;
    }
    window.zE("messenger", "loginUser", (callback: JwtFnCallback) => {
      getZendeskJWTMessaging().then(callback);
    });
    // set zIndex to 1000 to ensure the widget does not meddle with mui components, https://mui.com/material-ui/customization/z-index/
    window.zE("messenger:set", "zIndex", 1000);
  }, [webWidgetRendered]);

  useEffect(() => {
    if (!isFacility || admin) {
      return;
    }
    // Doc ref - https://support.zendesk.com/hc/en-us/articles/4500748175258-Installing-the-Web-Widget-in-a-website-or-Help-Center
    const script = document.createElement("script");
    script.id = meta().zendeskWebWidgetId ?? "";
    script.src = meta().zendeskMessagingWidgetUrl ?? "";
    document.body.appendChild(script);
    script.addEventListener("load", () => setWebWidgetRendered(true));

    return () => {
      document.body.removeChild(script);
      window.zE("messenger", "logoutUser");
    };
  }, [isFacility, admin]);

  return { webWidgetRendered };
};

export default useZendeskMessaging;
